import clsx from "clsx";
import { format, isAfter, isSameDay, isWeekend } from "date-fns";
import { de } from "date-fns/locale";
import TimeTrackingBar from "modules/timeTracking/components/components/Day/components/TimeTrackingBar/TimeTrackingBar";
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { Absence } from "~/modules/absence/api/absence/absenceTypes.ts";
import type { AbsenceType } from "~/modules/absence/api/absenceType/absenceTypeTypes.ts";
import type { Holiday } from "~/modules/absence/api/holiday/holidayTypes.ts";
import type { TimeTrackingExtendedType } from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import { AddTimeTrackingButton } from "~/modules/timeTracking/components/components/AddTimeTrackingButton.tsx";
import HourCount from "~/modules/timeTracking/components/components/Day/components/HourCount/HourCount";
import {
	getTimeTrackingBarTheme,
} from "~/modules/timeTracking/components/components/Day/components/TimeTrackingBar/getTimeTrackingBarTheme.ts";
import { getAbsencesOnDay } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";
import { sumUp } from "~/utils/sumUp.ts";

import { Details } from "./components/Details.tsx";

type DayProps = {
	allAbsenceTypes?: AbsenceType[];
	date: Date;
	holidays?: Holiday[];
	canAddTrackingOn: (date: Date) => boolean;
	showDisabledAddTracking: boolean;
	setScrollToElementRef?: (ref: HTMLDivElement) => void;
	openCreate: (date: Date) => void;
	openDelete: (timeTrackingId: string) => void;
	openUpdate: (timeTrackingId: string) => void;
	setSelectedTimeTrackingId: (id: string | null) => void;
	selectedDate: Date | null;
	selectedTimeTrackingId: string | null;
	showOpenDaysOnly: boolean;
	timeTrackingData?: TimeTrackingExtendedType[];
	userAbsencesData?: Absence[];
	users: User[];
	visible: boolean;
};

const maxMinutes = 480; // 12 hours
const today = new Date();
const Day: React.FC<DayProps> = ({
	allAbsenceTypes,
	date,
	holidays,
	canAddTrackingOn,
	showDisabledAddTracking,
	selectedDate,
	selectedTimeTrackingId,
	setScrollToElementRef,
	openCreate,
	openDelete,
	openUpdate,
	setSelectedTimeTrackingId,
	showOpenDaysOnly,
	timeTrackingData,
	userAbsencesData,
	visible,
}) => {
	const { t } = useTranslation();

	if (undefined === timeTrackingData) return null;

	const isWeekendDay = isWeekend(date);
	const trackingIsPossible = canAddTrackingOn(date);
	const holiday = holidays?.filter((h) => isSameDay(Date.parse(h.date), date));

	const className = `${!visible && isWeekendDay ? "hidden" : ""}`;

	const [absences, totalAbsenceHours] = useMemo(() => {
		const dayAbsences = getAbsencesOnDay({ absences: userAbsencesData, absenceTypes: allAbsenceTypes, date });
		return [
			dayAbsences.map(({ absenceName, isHalfDay }) => ({ absenceName, width: isHalfDay ? 50 : 100 })),
			sumUp(dayAbsences, "hours"),
		];
	}, [allAbsenceTypes, date, userAbsencesData]);

	const totalMinutes = sumUp(timeTrackingData, "minutes");
	const totalHours = totalMinutes / 60 + totalAbsenceHours;

	const isToday = isSameDay(date, new Date());
	const isTodayAndSelected = selectedDate && isSameDay(date, selectedDate);

	const dayTextColorClass = isTodayAndSelected ? "text-white" : isToday ? "text-secondary-500" : "text-gray-400";

	const hoursToFulfill = 8 - totalHours > 0 ? 8 - totalHours : 0;
	const hoursFulfilled = (timeTrackingData.length > 0 && totalHours >= hoursToFulfill) || hoursToFulfill === 0;

	if (showOpenDaysOnly && (isAfter(date, today) || hoursFulfilled)) return null;


	return (
		<div className={clsx(className, "flex flex-row border border-gray-50 py-4 pr-4 ", selectedDate && isSameDay(date, selectedDate) ? "bg-gray-400 text-white" : "")}
			 ref={isToday ? setScrollToElementRef : null}>
			<div className="flex w-16 shrink-0 cursor-pointer flex-col">
				<div
					className={clsx("text-center font-bold", dayTextColorClass, isToday ? "text-2xl" : "text-xl")}>{format(date, "dd", { locale: de })}</div>
				<div
					className={clsx("text-center", dayTextColorClass)}>{format(date, "eee", { locale: de })}</div>
			</div>

			<div className="flex w-full flex-row overflow-hidden rounded-md bg-gray-100 text-gray-500">
				<div className="flex max-w-full grow gap-2 p-2">
					{undefined !== holiday && 0 < holiday.length &&
						<TimeTrackingBar width={20}>{holiday[0].displayName}</TimeTrackingBar>}
					{absences && absences.map(({ absenceName, width }, index) => {
						return <TimeTrackingBar key={index}
												theme="absence"
												width={width}>{t(normalizeTKey("entities:absenceType." + absenceName))}</TimeTrackingBar>;
					})}
					{timeTrackingData.map((data) => {
						let width = 100;
						if (data.id !== selectedTimeTrackingId) {
							const base = totalMinutes > maxMinutes ? totalMinutes : maxMinutes;
							width = (data.minutes / base) * 100;
						}


						const { theme: barTheme, title } = getTimeTrackingBarTheme(data);

						return (
							<TimeTrackingBar
								key={"time-tracking-" + data.id}
								hours={formatNumberWithComma(data.minutes / 60, 1, false)}
								theme={barTheme}
								width={width}
								onClick={data.id === selectedTimeTrackingId ? () => setSelectedTimeTrackingId(null) : () => setSelectedTimeTrackingId(data.id)}
							>
								{data.id === selectedTimeTrackingId ? (
									<Details
										data={data}
										onClickDelete={() => openDelete(data.id)}
										onClickEdit={() => openUpdate(data.id)}
										onClickClose={() => setSelectedTimeTrackingId(null)}
									/>
								) : (
									width > 5 && (
										<span className="font-bold">
											{title}
										</span>
									)
								)}
							</TimeTrackingBar>
						);
					})}
					{(trackingIsPossible || showDisabledAddTracking) && (
						<AddTimeTrackingButton date={date}
											   canAddTracking={trackingIsPossible}
											   openCreate={openCreate} />
					)}
				</div>
			</div>
			{timeTrackingData.length > 0 && (
				<HourCount theme={hoursFulfilled ? "completed" : undefined}>{formatNumberWithComma(totalHours, 1, false)}
				</HourCount>
			)}
		</div>
	);
};

export default Day;